/**
 * VendorProfilePage.tsx
 * Individual vendor profile with products, reviews, and contact options
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useVendor, useVendorProducts } from '../hooks/useMarketplace';
import { ProductCard, StarRating, InquiryForm } from '../components';

export const VendorProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [showInquiry, setShowInquiry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const { data: vendor, isLoading: vendorLoading, error: vendorError } = useVendor(slug || '');
  const { data: productsData, isLoading: productsLoading } = useVendorProducts(
    vendor?.id || '',
    currentPage,
    pageSize
  );

  if (vendorLoading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3]">
        <div className="animate-pulse">
          {/* Banner skeleton */}
          <div className="h-64 bg-[#F5EDE4]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-6">
                <div className="w-32 h-32 rounded-xl bg-[#F5EDE4]" />
                <div className="flex-1 space-y-4">
                  <div className="h-8 w-64 bg-[#F5EDE4] rounded" />
                  <div className="h-4 w-96 bg-[#F5EDE4] rounded" />
                  <div className="h-4 w-48 bg-[#F5EDE4] rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (vendorError || !vendor) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#2B3A4D] mb-2">Vendor Not Found</h1>
          <p className="text-[#8B8178] mb-6">The vendor you're looking for doesn't exist.</p>
          <Link
            to="/marketplace/vendors"
            className="inline-flex items-center px-6 py-3 bg-[#7A9E7E] text-white rounded-xl hover:bg-[#5C7D60] transition-colors"
          >
            Browse All Vendors
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Banner */}
      <div 
        className="h-64 bg-cover bg-center relative"
        style={{ 
          backgroundImage: vendor.banner_url 
            ? `url(${vendor.banner_url})` 
            : 'linear-gradient(135deg, #7A9E7E 0%, #5C7D60 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Profile Card */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex flex-col lg:flex-row items-start gap-6">
            {/* Logo */}
            <div className="w-32 h-32 rounded-xl overflow-hidden bg-[#F5EDE4] flex-shrink-0 border-4 border-white shadow-md">
              {vendor.logo_url ? (
                <img 
                  src={vendor.logo_url} 
                  alt={vendor.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl font-semibold text-[#7A9E7E]">
                  {vendor.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-semibold text-[#2B3A4D] font-['Fraunces']">
                      {vendor.name}
                    </h1>
                    {vendor.is_verified && (
                      <span className="inline-flex items-center px-3 py-1 bg-[#7A9E7E]/10 text-[#5C7D60] text-sm font-medium rounded-full">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-[#8B8178] mb-3">
                    <div className="flex items-center gap-1">
                      <StarRating rating={vendor.avg_rating ?? 0} size="sm" />
                      <span className="ml-1 font-medium text-[#2B3A4D]">{(vendor.avg_rating ?? 0).toFixed(1)}</span>
                      <span>({vendor.review_count ?? 0} reviews)</span>
                    </div>
                    {vendor.years_in_business && (
                      <span>• {vendor.years_in_business} years in business</span>
                    )}
                  </div>

                  <p className="text-[#445566] max-w-2xl">{vendor.description}</p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowInquiry(true)}
                    className="px-6 py-3 bg-[#7A9E7E] text-white rounded-xl font-medium hover:bg-[#5C7D60] transition-colors"
                  >
                    Contact Vendor
                  </button>
                  {vendor.website && (
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 border-2 border-[#E5E0DB] text-[#2B3A4D] rounded-xl font-medium hover:border-[#7A9E7E] transition-colors"
                    >
                      Visit Website
                    </a>
                  )}
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6 pt-6 border-t border-[#E5E0DB]">
                {vendor.phone && (
                  <div>
                    <dt className="text-xs font-medium text-[#8B8178] uppercase tracking-wide">Phone</dt>
                    <dd className="mt-1 text-[#2B3A4D]">
                      <a href={`tel:${vendor.phone}`} className="hover:text-[#7A9E7E]">
                        {vendor.phone}
                      </a>
                    </dd>
                  </div>
                )}
                {vendor.email && (
                  <div>
                    <dt className="text-xs font-medium text-[#8B8178] uppercase tracking-wide">Email</dt>
                    <dd className="mt-1 text-[#2B3A4D]">
                      <a href={`mailto:${vendor.email}`} className="hover:text-[#7A9E7E]">
                        {vendor.email}
                      </a>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs font-medium text-[#8B8178] uppercase tracking-wide">Service Area</dt>
                  <dd className="mt-1 text-[#2B3A4D]">
                    {vendor.service_area_national 
                      ? 'Nationwide' 
                      : vendor.service_area_states?.join(', ') || 'Local'}
                  </dd>
                </div>
                {vendor.insurance_accepted && vendor.insurance_accepted.length > 0 && (
                  <div>
                    <dt className="text-xs font-medium text-[#8B8178] uppercase tracking-wide">Insurance</dt>
                    <dd className="mt-1 text-[#2B3A4D]">
                      {vendor.insurance_accepted.slice(0, 2).join(', ')}
                      {vendor.insurance_accepted.length > 2 && ` +${vendor.insurance_accepted.length - 2} more`}
                    </dd>
                  </div>
                )}
              </div>

              {/* Categories & Certifications */}
              <div className="flex flex-wrap gap-2 mt-4">
                {vendor.certifications?.map((cert, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1 bg-[#C4785A]/10 text-[#C4785A] text-sm rounded-full"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-[#2B3A4D] font-['Fraunces']">
              Products & Services
            </h2>
            <span className="text-[#8B8178]">
              {productsData?.total || 0} items
            </span>
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                  <div className="aspect-square bg-[#F5EDE4] rounded-lg mb-4" />
                  <div className="h-4 bg-[#F5EDE4] rounded w-3/4 mb-2" />
                  <div className="h-4 bg-[#F5EDE4] rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : productsData?.items && productsData.items.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {productsData.items.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {(productsData.totalPages ?? productsData.total_pages ?? 1) > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {[...Array(productsData.totalPages ?? productsData.total_pages ?? 1)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        currentPage === i + 1
                          ? 'bg-[#7A9E7E] text-white'
                          : 'bg-white text-[#2B3A4D] hover:bg-[#F5EDE4]'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center">
              <p className="text-[#8B8178]">No products available from this vendor yet.</p>
            </div>
          )}
        </div>

        {/* Gallery */}
        {vendor.gallery_urls && vendor.gallery_urls.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2B3A4D] font-['Fraunces'] mb-6">
              Gallery
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {vendor.gallery_urls.map((url, idx) => (
                <div key={idx} className="aspect-square rounded-xl overflow-hidden">
                  <img 
                    src={url} 
                    alt={`${vendor.name} gallery ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Inquiry Modal */}
      {showInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E0DB] flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#2B3A4D]">Contact {vendor.name}</h3>
              <button 
                onClick={() => setShowInquiry(false)}
                className="p-2 hover:bg-[#F5EDE4] rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <InquiryForm 
                vendorId={vendor.id}
                vendorName={vendor.name}
                onSuccess={() => setShowInquiry(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorProfilePage;


